// react
import { Suspense, useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import BoltIcon from '@mui/icons-material/Bolt';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';

// material ui
import { Box, CircularProgress, Grid, Skeleton, useTheme } from '@mui/material';

// api
import { useGetLinkedEnergyGroupsLinkedEnergyGroupsAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';
// api — generated hooks
import { useGetSubscriptionSubscriptionAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisSubscription';

// shared components
import PageContainer from '@/components/layout/PageContainer';

// components
import PortfolioAnalysisDashboardPresetTabs from '@/features/energyxplorer/portfolio/components/datagrid/overview-datagrid/PortfolioAnalysisDashboardPresetTabs';
import PortfolioAnalysisServiceCard from '@/features/energyxplorer/portfolio/components/datagrid/PortfolioAnalysisServiceCard';
import DataIntegrityDialog from '@/features/energyxplorer/portfolio/components/meter-drawer/data-integrity/DataIntegrityDialog';
import MeterDrawer from '@/features/energyxplorer/portfolio/components/meter-drawer/MeterDrawer';
import useMatrikelList from '@/features/energyxplorer/portfolio/hooks/useMatrikelList';
import { meanByType } from '@/features/energyxplorer/portfolio/utils/portfolio-analysis-helpers';
import type {
  AnalysisType,
  EnrichedMeter,
} from '@/features/energyxplorer/types';
import useSearchParams from '@/hooks/useStableSearchParams';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

interface PortfolioAnalysisDashboardProps {
  DatagridComponent: ComponentType<Record<string, unknown>>;
}

/**
 * Shared portfolio analysis dashboard. Renders the service overview cards,
 * preset tabs and a datagrid provided by the caller via `DatagridComponent`
 * (e.g. the "standard" or "building" grouped grid).
 */
const PortfolioAnalysisDashboard = ({
  DatagridComponent,
}: PortfolioAnalysisDashboardProps) => {
  const theme = useTheme();
  const { t } = useTranslation('portfolio-analysis_dashboard');
  const [searchParams] = useSearchParams();
  const isFullscreen = searchParams.get('fullscreen') === 'true';
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState<
    Record<string, unknown> | undefined
  >();

  const handleSetSelectedMeter = (meter: Record<string, unknown>) => {
    setIsOpen(true);
    setSelectedMeter(meter);
  };

  const [meterType, setMeterType] = useState<string>('');
  const account = useSelector(getSelectedAccount);

  const { data: subscriptionData } =
    useGetSubscriptionSubscriptionAccountIdGetQuery(
      { accountId: account?.AccountId },
      { skip: !account?.AccountId },
    );

  const {
    matrikel_list,
    isFetching,
    powerSummedData,
    heatSummedData,
    waterSummedData,
  } = useMatrikelList();

  const { data: linkedEnergyGroupsData } =
    useGetLinkedEnergyGroupsLinkedEnergyGroupsAccountIdGetQuery(
      { accountId: account?.AccountId },
      {
        skip: !account?.AccountId,
      },
    );

  const rows = useMemo(() => {
    if (!matrikel_list?.length) return { portfolio_analysis_list: [] };

    const getTypeTotal = (
      item: (typeof matrikel_list)[number],
      energyType: 'power' | 'water' | 'heat',
    ) => {
      const typeData = item[energyType];
      const analysis =
        (typeData?.matrikel_analysis as Record<string, unknown>) ?? {};
      const fromAnalysis = analysis.total_consumption_year as
        | number
        | undefined;
      if (fromAnalysis != null) return fromAnalysis;
      return typeData?.meter_list
        ?.filter((e) => e.hasAnalysis)
        .reduce(
          (sum, e) =>
            sum + ((e.analysis.total_consumption_year ?? 0) as number),
          0,
        );
    };

    const getTypeCurrentYear = (
      item: (typeof matrikel_list)[number],
      energyType: 'power' | 'water' | 'heat',
    ) => {
      const typeData = item[energyType];
      const analysis =
        (typeData?.matrikel_analysis as Record<string, unknown>) ?? {};
      const fromAnalysis = analysis.current_calendar_year_sum as
        | number
        | undefined;
      if (fromAnalysis != null) return fromAnalysis;
      return typeData?.meter_list
        ?.filter((e) => e.hasAnalysis)
        .reduce(
          (sum, e) =>
            sum + ((e.analysis.current_calendar_year_sum ?? 0) as number),
          0,
        );
    };

    // One row per matrikel with all three energy types' totals merged
    const list = matrikel_list
      .filter((item) =>
        (['power', 'water', 'heat'] as const).some(
          (et) =>
            (item[et].meter_list?.length ?? 0) > 0 ||
            Object.keys(
              (item[et].matrikel_analysis as Record<string, unknown>) ?? {},
            ).length > 0,
        ),
      )
      .map((item) => {
        const { metadata } = item;
        // Use power matrikel_analysis for common fields (occupancy, etc.) with heat/water as fallback
        const primaryAnalysis =
          (item.power.matrikel_analysis as Record<string, unknown>) ??
          (item.heat.matrikel_analysis as Record<string, unknown>) ??
          {};

        return {
          ...primaryAnalysis,
          matrikel_key: metadata.MatrikelKey,
          display_name: metadata.Address || metadata.MainAddress,
          main_address: metadata.Address || metadata.MainAddress,
          type: 'matrikel',
          power_total_consumption_year: getTypeTotal(item, 'power'),
          heat_total_consumption_year: getTypeTotal(item, 'heat'),
          water_total_consumption_year: getTypeTotal(item, 'water'),
          power_current_calendar_year_sum: getTypeCurrentYear(item, 'power'),
          heat_current_calendar_year_sum: getTypeCurrentYear(item, 'heat'),
          water_current_calendar_year_sum: getTypeCurrentYear(item, 'water'),
          SumBuildingArea: metadata.OverwriteFlag
            ? (metadata.OverwriteSumBuildingArea ?? metadata.SumBuildingArea)
            : metadata.SumBuildingArea,
          ExtractedBuildingUse: metadata.OverwriteFlag
            ? (metadata.OverwriteExtractedBuildingUse ??
              metadata.ExtractedBuildingUse)
            : metadata.ExtractedBuildingUse,
        } as Record<string, unknown>;
      });

    return { portfolio_analysis_list: list };
  }, [matrikel_list]);

  const enrichedData = useMemo(() => {
    const linkedEnergyGroups = linkedEnergyGroupsData?.LinkedEnergyGroups || [];
    if (!rows.portfolio_analysis_list.length) return null;
    const temp = {
      ...rows,
      portfolio_analysis_list: rows.portfolio_analysis_list?.map(
        (o) =>
          (({
            ...o,

            linked_energy_groups: linkedEnergyGroups
              ?.find((leg) => leg.MatrikelKey === o.matrikel_key)
              ?.LinkedEnergyGroups.filter(
                (group) => !group.type?.toLowerCase().includes('portfolio'),
              )
          }) as Record<string, unknown>),
      ),
    };
    return temp;
  }, [rows, linkedEnergyGroupsData]);

  if (!enrichedData) return null;

  // Cast to any[] so service-card reduce/filter/map can access dynamic fields
  const rows$ = enrichedData.portfolio_analysis_list as Array<
    Record<string, unknown>
  >;

  return (
    <PageContainer>
      <Grid container spacing={2} maxHeight={'80vh'}>
        <Suspense fallback={<Skeleton />}>
          {subscriptionData &&
            !isFullscreen &&
            [
              {
                disabled: !subscriptionData?.power,
                label: t('cards.power.title'),
                path: '../../power/analysis/overview',
                value: rows$.reduce(
                  (acc, o) =>
                    o.power_total_consumption_year
                      ? acc + (o.power_total_consumption_year as number)
                      : acc,
                  0,
                ),
                unit: t('cards.power.unit'),
                icon: <BoltIcon />,
                color: theme.palette.power.main,
                dataPct: meanByType(rows$, 'power', 'one_year_data_pct'),
                area: rows$.reduce(
                  (acc, o) =>
                    o.SumBuildingArea && o.type === 'power'
                      ? acc + (o.SumBuildingArea as number)
                      : acc,
                  0,
                ),
                buildingCount: new Set(
                  rows$
                    .filter((item) => item.type === 'power')
                    .map((item) => item.main_address),
                ).size,
                summedData: powerSummedData ?? [],
              },
              {
                disabled: !subscriptionData?.heat,
                label: t('cards.heat.title'),
                path: '../../heat/analysis/overview',
                value: rows$.reduce(
                  (acc, o) =>
                    o.heat_total_consumption_year
                      ? acc + (o.heat_total_consumption_year as number)
                      : acc,
                  0,
                ),
                unit: t('cards.heat.unit'),
                icon: <LocalFireDepartmentRoundedIcon />,
                color: theme.palette.heat.main,
                area: rows$.reduce(
                  (acc, o) =>
                    o.SumBuildingArea && o.type === 'heat'
                      ? acc + (o.SumBuildingArea as number)
                      : acc,
                  0,
                ),
                dataPct: meanByType(rows$, 'heat', 'one_year_data_pct'),
                buildingCount: new Set(
                  rows$
                    .filter((item) => item.type === 'heat')
                    .map((item) => item.main_address),
                ).size,
                summedData: heatSummedData ?? [],
              },
              {
                disabled: !subscriptionData?.water,
                label: t('cards.water.title'),
                path: '../../water/analysis/overview',
                value: rows$.reduce(
                  (acc, o) =>
                    o.water_total_consumption_year
                      ? acc + (o.water_total_consumption_year as number)
                      : acc,
                  0,
                ),
                unit: t('cards.water.unit'),
                icon: <WaterDropRoundedIcon />,
                color: theme.palette.water.main,
                dataPct: meanByType(rows$, 'water', 'one_year_data_pct'),
                area: rows$.reduce(
                  (acc, o) =>
                    o.SumBuildingArea && o.type === 'water'
                      ? acc + (o.SumBuildingArea as number)
                      : acc,
                  0,
                ),
                buildingCount: new Set(
                  rows$
                    .filter((item) => item.type === 'water')
                    .map((item) => item.main_address),
                ).size,
                summedData: waterSummedData ?? [],
              },
            ].map((service) => (
              <Grid
                key={service.label}
                size={{
                  md: 12,
                  lg: 4
                }}>
                <PortfolioAnalysisServiceCard {...service} />
              </Grid>
            ))}
        </Suspense>
        <Grid maxHeight={'80vh'} size={12}>
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: '80vh',
                  verticalAlign: 'center',
                }}
              >
                <CircularProgress />
              </Box>
            }
          >
            <Box
              maxHeight={isFullscreen ? '80vh' : '60vh'}
              display={'flex'}
              flexDirection={'column'}
            >
              <PortfolioAnalysisDashboardPresetTabs />
              <Box sx={{ mb: 2 }} />
              <DatagridComponent
                data={enrichedData}
                matrikelList={matrikel_list}
                setSelectedMeter={handleSetSelectedMeter}
                setMeterType={setMeterType}
                outOfSync={false}
                isLoading={isFetching}
              />
            </Box>
          </Suspense>
        </Grid>
      </Grid>
      {selectedMeter && (
        <Suspense
          fallback={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <MeterDrawer
            key={String(
              ((selectedMeter?.type as AnalysisType) || 'power') === 'power'
                ? selectedMeter.metering_point_id
                : ((
                    selectedMeter.data_stream as
                      | Record<string, unknown>
                      | undefined
                  )?.unit_id ?? selectedMeter?.matrikel_key),
            )}
            open={isOpen}
            onClose={() => setIsOpen(false)}
            meter={selectedMeter as EnrichedMeter & Record<string, unknown>}
            meterType={meterType as 'meter' | 'matrikel'}
            analysisType={(selectedMeter?.type as AnalysisType) || 'power'}
          />
        </Suspense>
      )}
      <DataIntegrityDialog />
    </PageContainer>
  );
};

export default PortfolioAnalysisDashboard;
